import { Component, HostListener, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { SsModuleService, YeehroCalendlyBookingService } from '@app/services';

// First step of the book-meeting flow: who is the meeting with?
//
// Replaces the old "Select Record Type" dialog on that flow. A host can still
// pick a CRM record type and go on to the record search, or skip the CRM
// entirely and type a guest's name and email — a meeting with someone who was
// never a lead (a school contact, a reference, a vendor) no longer needs a
// throwaway record created first. The booking model already allows a booking
// with no relatedRecord (that is how public standalone bookings save), so the
// guest path just leaves it off.
//
// Resolves with:
//   { mode: 'record', moduleType }                  - caller opens the record picker next
//   { mode: 'guest', guest: { name, email, phone } } - caller skips straight to the event picker
//   undefined                                        - cancelled
@Component({
  selector: 'booking-attendee-dialog',
  templateUrl: './booking-attendee-dialog.component.html',
  styleUrls: ['./booking-attendee-dialog.component.scss'],
  standalone: false
})
export class BookingAttendeeDialogComponent implements OnInit {
  title: string = 'Who is this meeting with?';

  mode: 'record' | 'guest' = 'record';

  // Record mode. Only a handful of modules are bookable, so they render as a
  // list of choices rather than a dropdown.
  modules: any[] = [];
  selectedModule: any = null;
  loadingModules: boolean = true;

  // Guest mode.
  guestName: string = '';
  guestEmail: string = '';
  guestPhone: string = '';
  showGuestErrors: boolean = false;

  constructor(
    private _dialogRef: MatDialogRef<BookingAttendeeDialogComponent>,
    private _ssModuleService: SsModuleService,
    private _bookingService: YeehroCalendlyBookingService
  ) {}


  async ngOnInit() {
    try {
      if (!this._ssModuleService.allModules) {
        this._ssModuleService.allModules = await lastValueFrom(this._ssModuleService.getAll());
      }

      const accepted = this._bookingService.bookableRecordModules || [];
      const allModules = this._ssModuleService.allModules || [];

      this.modules = allModules
        .filter(_module => accepted.includes(_module?.name))
        .sort((_a, _b) => String(_a?.label || '').localeCompare(String(_b?.label || '')));
    } catch (_err) {
      console.log('Error loading modules in booking-attendee-dialog: ', _err);
      this.modules = [];
    } finally {
      this.loadingModules = false;
    }
  }


  @HostListener('document:keydown.escape')
  onEscape() {
    this.onCancel();
  }


  setMode(mode: 'record' | 'guest') {
    this.mode = mode;
    this.showGuestErrors = false;
  }


  selectModule(_module: any) {
    this.selectedModule = _module;
  }


  isModuleSelected(_module: any): boolean {
    return !!_module && this.selectedModule?._id === _module?._id;
  }


  get guestNameInvalid(): boolean {
    return !this.guestName?.trim();
  }


  // Deliberately loose: the confirmation email has to have somewhere to go, but
  // this is not the place to argue with a host about an unusual address.
  get guestEmailInvalid(): boolean {
    const email = this.guestEmail?.trim() || '';
    return !email.length || !/^\S+@\S+\.\S+$/.test(email);
  }


  get canContinue(): boolean {
    if (this.mode === 'record') return !!this.selectedModule?.name;
    return !this.guestNameInvalid && !this.guestEmailInvalid;
  }


  onCancel() {
    this._dialogRef.close(undefined);
  }


  onContinue() {
    if (this.mode === 'record') {
      if (!this.selectedModule?.name) return;
      this._dialogRef.close({ "mode": 'record', "moduleType": this.selectedModule });
      return;
    }

    if (!this.canContinue) {
      this.showGuestErrors = true;
      return;
    }

    this._dialogRef.close({
      "mode": 'guest',
      "guest": {
        "name": this.guestName.trim(),
        "email": this.guestEmail.trim(),
        "phone": this.guestPhone?.trim() || ''
      }
    });
  }
}
